import { useState } from "react";
import { useNavigate } from "react-router-dom";

export default function Login() {
  const [userType, setUserType] = useState<"student" | "teacher" | "admin">(
    "student"
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseKey) {
        throw new Error(
          "Cấu hình hệ thống chưa đầy đủ. Vui lòng liên hệ quản trị viên."
        );
      }

      // Thử gọi Edge Function với timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 giây timeout

      try {
        const response = await fetch(`${supabaseUrl}/functions/v1/auth-login`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${supabaseKey}`,
          },
          body: JSON.stringify({ email, password }),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error(
              "Tài khoản không tồn tại. Vui lòng kiểm tra lại hoặc liên hệ quản trị viên."
            );
          }
          throw new Error("Email hoặc mật khẩu không đúng.");
        }

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          throw new Error("Phản hồi từ server không hợp lệ.");
        }

        const data = await response.json();

        if (data.error) {
          throw new Error(data.error);
        }

        const accessToken = data.session?.access_token || supabaseKey;

        const userProfile = {
          id: data.user.id,
          email: data.user.email,
          full_name: data.user.name,
          user_type: data.user.type,
          token: accessToken,
          created_at: new Date().toISOString(),
        };

        const sessionData = {
          access_token: accessToken,
          user: data.user,
          ...data.session,
        };

        localStorage.setItem("supabase_session", JSON.stringify(sessionData));
        localStorage.setItem("userProfile", JSON.stringify(userProfile));
        localStorage.setItem("userType", data.user.type);
        localStorage.setItem("userEmail", data.user.email);

        // Chuyển hướng theo loại người dùng
        switch (data.user.type) {
          case "admin":
            navigate("/admin");
            break;
          case "teacher":
            navigate("/teacher");
            break;
          case "student":
            navigate("/student");
            break;
          default:
            navigate("/");
        }
      } catch (fetchError) {
        clearTimeout(timeoutId);

        if (fetchError instanceof Error && fetchError.name === "AbortError") {
          throw new Error(
            "Kết nối quá chậm. Vui lòng thử lại sau hoặc liên hệ quản trị viên."
          );
        }

        throw fetchError;
      }
    } catch (err: unknown) {
      let errorMessage = "Đã xảy ra lỗi. Vui lòng thử lại.";

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === "string") {
        errorMessage = err;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <img
            src="/favicon.png"
            alt="Olympic Academy"
            className="w-28 mx-auto mb-4 drop-shadow-md"
          />
          <h2 className="text-3xl text-gray-900 mb-2">OLYMPIC ACADEMY</h2>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Đăng nhập</h1>
          {/* <p className="text-gray-600">Học viện Olympic Academy</p> */}
        </div>

        {/* Tabs chọn loại người dùng (chỉ đổi giao diện, backend vẫn xác định type) */}
        <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
          <button
            type="button"
            onClick={() => setUserType("student")}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
              userType === "student"
                ? "bg-blue-600 text-white shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Học sinh
          </button>
          <button
            type="button"
            onClick={() => setUserType("teacher")}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
              userType === "teacher"
                ? "bg-blue-600 text-white shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Giáo viên
          </button>
          <button
            type="button"
            onClick={() => setUserType("admin")}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
              userType === "admin"
                ? "bg-blue-600 text-white shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Admin
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="Nhập email của bạn"
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Mật khẩu
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="Nhập mật khẩu"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Đang đăng nhập...
              </div>
            ) : (
              "Đăng nhập"
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate("/")}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            ← Quay về trang chủ
          </button>
        </div>
      </div>
    </div>
  );
}
